import { useEffect, useMemo, useRef, useState } from "react";
import { logShare } from "@/services/affiliate";
import { fetchProspectsForImport, webinarLink } from "@/services/webinar";
import { buildMessage, DIALS, normalizePhone, readNumber, writeNumber } from "./shared";

const PURPLE = "#7C3AED";
const DAILY_CAP = 40;

export const ORIGINS: Record<string, { label: string; icon: string; cls: string }> = {
  telefono: { label: "Lista telefónica", icon: "ri-contacts-book-line", cls: "bg-slate-100 text-slate-700" },
  whatsapp: { label: "WhatsApp", icon: "ri-whatsapp-fill", cls: "bg-emerald-50 text-emerald-700" },
  instagram: { label: "Instagram", icon: "ri-instagram-line", cls: "bg-pink-50 text-pink-700" },
  facebook: { label: "Facebook", icon: "ri-facebook-circle-fill", cls: "bg-blue-50 text-blue-700" },
  tiktok: { label: "TikTok", icon: "ri-tiktok-fill", cls: "bg-slate-100 text-slate-800" },
  linkedin: { label: "LinkedIn", icon: "ri-linkedin-box-fill", cls: "bg-sky-50 text-sky-700" },
  youtube: { label: "YouTube", icon: "ri-youtube-fill", cls: "bg-red-50 text-red-700" },
  telegram: { label: "Telegram", icon: "ri-telegram-fill", cls: "bg-cyan-50 text-cyan-700" },
  otro: { label: "Otra red", icon: "ri-global-line", cls: "bg-slate-100 text-slate-700" },
};

export const VIAS: Record<string, string> = {
  agenda: "Agenda del teléfono",
  archivo: "Archivo (CSV / vCard)",
  manual: "Manual",
  scraper: "Scraper",
  referido: "Referido",
  evento: "Evento",
  webinar: "Webinar",
  otra: "Otra vía",
};

const INTERVALS = [
  { key: "10", label: "10 segundos", min: 10, max: 10 },
  { key: "30", label: "30 segundos", min: 30, max: 30 },
  { key: "60", label: "1 minuto", min: 60, max: 60 },
  { key: "var", label: "Variable (10–60 s)", min: 10, max: 60 },
];

type Contact = { id: string; name: string; phone: string; origin: string; via: string; status: "pendiente" | "enviado" };
type Cand = { key: string; name: string; phone: string; selected: boolean; origin: string; via: string; fixed?: boolean; note?: string };
type Tab = "agenda" | "pegar" | "prospectos";
type PickerApi = { select: (props: string[], opts: { multiple: boolean }) => Promise<{ name?: string[]; tel?: string[] }[]> };

const sentKey = () => `vv-wb-sent-${new Date().toISOString().slice(0, 10)}`;
const looksPhone = (v: string) => (v.match(/\d/g)?.length ?? 0) >= 7 && /^[+\d\s().-]+$/.test(v.trim());

/** CSV / texto libre: una persona por línea. Detecta la celda que parece teléfono. */
function parseRows(text: string): { name: string; phone: string }[] {
  const out: { name: string; phone: string }[] = [];
  for (const line of text.split(/\r?\n/)) {
    const l = line.trim();
    if (!l) continue;
    const sep = l.includes(";") ? ";" : l.includes("\t") ? "\t" : ",";
    const cells = l.split(sep).map((c) => c.replace(/^"|"$/g, "").trim());
    let phone = cells.find(looksPhone);
    let name = cells.filter((c) => c && c !== phone && !looksPhone(c))[0] ?? "";
    if (!phone) {
      const m = l.match(/^(.*?)[\s,;]+(\+?\d[\d\s().-]{6,})$/);
      if (m) {
        name = m[1].replace(/["]/g, "").trim();
        phone = m[2];
      }
    }
    if (phone) out.push({ name, phone });
  }
  return out;
}

function parseVcf(text: string): { name: string; phone: string }[] {
  const out: { name: string; phone: string }[] = [];
  for (const card of text.split(/BEGIN:VCARD/i).slice(1)) {
    const fn = card.match(/^FN[^:]*:(.+)$/im)?.[1]?.trim() ?? "";
    const tel = card.match(/^TEL[^:]*:(.+)$/im)?.[1]?.trim() ?? "";
    if (tel) out.push({ name: fn, phone: tel });
  }
  return out;
}

function Chip({ origin, via }: { origin: string; via: string }) {
  const o = ORIGINS[origin] ?? ORIGINS.otro;
  return (
    <span className="inline-flex flex-wrap gap-1">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold ${o.cls}`}>
        <i className={o.icon}></i>
        {o.label}
      </span>
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-violet-50 text-violet-700">{VIAS[via] ?? via}</span>
    </span>
  );
}

export default function BulkSender({ code, templates, tpl }: { code: string | null; templates: string[]; tpl: string }) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [dial, setDial] = useState("57");
  const [intervalKey, setIntervalKey] = useState("30");
  const [rotate, setRotate] = useState(true);
  const [wait, setWait] = useState(0);
  const [sentToday, setSentToday] = useState(() => readNumber(sentKey()));
  const [fOrigin, setFOrigin] = useState("");
  const [fVia, setFVia] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const rot = useRef(0);

  // Importación
  const [importing, setImporting] = useState(false);
  const [tab, setTab] = useState<Tab>("agenda");
  const [cands, setCands] = useState<Cand[]>([]);
  const [paste, setPaste] = useState("");
  const [batchOrigin, setBatchOrigin] = useState("telefono");
  const [batchVia, setBatchVia] = useState("agenda");
  const [search, setSearch] = useState("");
  const [loadingP, setLoadingP] = useState(false);
  const [impErr, setImpErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const link = code ? webinarLink(code, "whatsapp") : "";
  const canPick = typeof navigator !== "undefined" && "contacts" in navigator && typeof window !== "undefined" && "ContactsManager" in window;

  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);

  useEffect(() => {
    if (!importing) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setImporting(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [importing]);

  const openImport = () => {
    setImporting(true);
    switchTab("agenda");
  };

  const switchTab = (t: Tab) => {
    setTab(t);
    setCands([]);
    setSearch("");
    setImpErr(null);
    if (t === "agenda") {
      setBatchOrigin("telefono");
      setBatchVia("agenda");
    } else if (t === "pegar") {
      setBatchOrigin("whatsapp");
      setBatchVia("manual");
    } else {
      setLoadingP(true);
      fetchProspectsForImport()
        .then((list) =>
          setCands(
            list.map((p) => ({
              key: p.id,
              name: p.name,
              phone: "",
              selected: false,
              origin: p.network && ORIGINS[p.network] ? p.network : "otro",
              via: p.source && /apify|scrap/i.test(p.source) ? "scraper" : "manual",
              fixed: true,
              note: p.campaign || p.niche || undefined,
            })),
          ),
        )
        .catch(() => setImpErr("No pudimos cargar tus prospectos."))
        .finally(() => setLoadingP(false));
    }
  };

  const addCands = (rows: { name: string; phone: string }[], via: string) => {
    if (!rows.length) return setImpErr("No encontramos contactos con número.");
    setImpErr(null);
    setCands(rows.map((r, i) => ({ key: `${i}-${r.phone}`, name: r.name, phone: r.phone, selected: false, origin: batchOrigin, via })));
  };

  const pickFromPhone = async () => {
    try {
      const api = (navigator as unknown as { contacts: PickerApi }).contacts;
      const res = await api.select(["name", "tel"], { multiple: true });
      addCands(res.flatMap((r) => (r.tel ?? []).slice(0, 1).map((t) => ({ name: r.name?.[0] ?? "", phone: t }))), "agenda");
    } catch {
      // canceló
    }
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) return setImpErr("El archivo es muy grande (máximo 3 MB).");
    const text = await f.text();
    addCands(/BEGIN:VCARD/i.test(text) ? parseVcf(text) : parseRows(text), "archivo");
    if (fileRef.current) fileRef.current.value = "";
  };

  const readPaste = () => addCands(parseRows(paste), "manual");

  const applyBatch = (o: string, v: string) => {
    setBatchOrigin(o);
    setBatchVia(v);
    setCands((cur) => cur.map((c) => (c.fixed ? c : { ...c, origin: o, via: v })));
  };

  const valid = (c: Cand) => !!normalizePhone(c.phone, dial);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cands.filter((c) => !q || c.name.toLowerCase().includes(q) || c.phone.includes(q));
  }, [cands, search]);
  const selected = cands.filter((c) => c.selected && valid(c));

  const toggleAll = (on: boolean) => {
    const keys = new Set(visible.map((c) => c.key));
    setCands((cur) => cur.map((c) => (keys.has(c.key) ? { ...c, selected: on && valid(c) } : c)));
  };

  const doImport = () => {
    const have = new Set(contacts.map((c) => c.phone));
    const added: Contact[] = [];
    let skipped = 0;
    for (const c of selected) {
      const phone = normalizePhone(c.phone, dial);
      if (!phone || have.has(phone)) {
        skipped++;
        continue;
      }
      have.add(phone);
      added.push({ id: `${phone}-${Date.now()}-${added.length}`, name: c.name.trim(), phone, origin: c.origin, via: c.via, status: "pendiente" });
    }
    setContacts((cur) => [...cur, ...added]);
    setImporting(false);
    flash(`${added.length} contacto(s) importado(s)${skipped ? ` · ${skipped} repetido(s)` : ""}`);
  };

  // Cola de envío
  const shown = contacts.filter((c) => (!fOrigin || c.origin === fOrigin) && (!fVia || c.via === fVia));
  const pending = shown.filter((c) => c.status === "pendiente");
  const next = pending[0];
  const capReached = sentToday >= DAILY_CAP;
  const usedOrigins = [...new Set(contacts.map((c) => c.origin))];
  const usedVias = [...new Set(contacts.map((c) => c.via))];

  const sendNext = () => {
    if (!next || wait > 0 || capReached || !code) return;
    const pool = rotate ? templates : [tpl];
    const t = pool[rot.current % pool.length];
    rot.current += 1;
    window.open(`https://wa.me/${next.phone}?text=${encodeURIComponent(buildMessage(t, next.name, link))}`, "_blank", "noopener");
    setContacts((cur) => cur.map((c) => (c.id === next.id ? { ...c, status: "enviado" } : c)));
    const n = sentToday + 1;
    setSentToday(n);
    writeNumber(sentKey(), n);
    void logShare("whatsapp");
    const iv = INTERVALS.find((i) => i.key === intervalKey) ?? INTERVALS[1];
    setWait(iv.min + Math.floor(Math.random() * (iv.max - iv.min + 1)));
  };

  const fieldCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-violet-400";

  return (
    <div className="mt-6 pt-5 border-t border-slate-100">
      <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2">
        <i className="ri-send-plane-line" style={{ color: PURPLE }}></i> Enviar a varios contactos
      </h3>
      <p className="text-xs text-slate-500 mt-0.5">Importa tus contactos, elige cuáles, y cada mensaje se abre en tu WhatsApp con el nombre ya puesto; tú tocas enviar. Entre uno y otro hay una pausa para cuidar tu número.</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={openImport} className="px-3.5 py-2 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap" style={{ background: PURPLE }}>
          <i className="ri-download-2-line mr-1.5"></i>Importar contactos
        </button>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          País por defecto
          <select value={dial} onChange={(e) => setDial(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs">
            {DIALS.map((d) => (
              <option key={d.d} value={d.d}>
                {d.n} (+{d.d})
              </option>
            ))}
          </select>
        </label>
      </div>

      {contacts.length > 0 ? (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <label className="text-xs font-semibold text-slate-600">
              Pausa entre mensajes
              <select value={intervalKey} onChange={(e) => setIntervalKey(e.target.value)} className={`${fieldCls} mt-1`}>
                {INTERVALS.map((i) => (
                  <option key={i.key} value={i.key}>
                    {i.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Enviar solo a origen
              <select value={fOrigin} onChange={(e) => setFOrigin(e.target.value)} className={`${fieldCls} mt-1`}>
                <option value="">Todos</option>
                {usedOrigins.map((o) => (
                  <option key={o} value={o}>
                    {ORIGINS[o]?.label ?? o}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-600">
              Enviar solo a vía
              <select value={fVia} onChange={(e) => setFVia(e.target.value)} className={`${fieldCls} mt-1`}>
                <option value="">Todas</option>
                {usedVias.map((v) => (
                  <option key={v} value={v}>
                    {VIAS[v] ?? v}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} className="accent-violet-600" />
            Alternar mis versiones del mensaje
          </label>

          <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/60 p-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[10rem] text-sm">
              {next ? (
                <>
                  <div className="font-bold text-slate-900">Siguiente: {next.name || "Sin nombre"}</div>
                  <div className="mt-0.5">
                    <Chip origin={next.origin} via={next.via} />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">+{next.phone} · quedan {pending.length}</div>
                </>
              ) : (
                <div className="font-bold text-slate-900">{shown.length ? "Lista terminada 🎉" : "Ningún contacto con ese filtro"}</div>
              )}
            </div>
            <button onClick={sendNext} disabled={!next || wait > 0 || capReached || !code} className="px-4 py-2.5 rounded-lg bg-[#25D366] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50">
              <i className="ri-whatsapp-fill mr-1.5"></i>
              {wait > 0 ? `Espera ${wait} s` : "Abrir y enviar"}
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Enviados hoy: {sentToday}/{DAILY_CAP}. {capReached ? "Llegaste al límite diario; sigue mañana." : "Este tope diario protege tu número de WhatsApp."}
          </p>

          <ul className="mt-3 max-h-64 overflow-y-auto divide-y divide-slate-100 rounded-xl border border-slate-100">
            {shown.map((c) => (
              <li key={c.id} className="px-3 py-2 flex items-center gap-2 text-sm">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.status === "enviado" ? "bg-emerald-500" : "bg-slate-300"}`}></span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-slate-800">
                    {c.name || "Sin nombre"} <span className="text-[11px] text-slate-400">+{c.phone}</span>
                  </span>
                  <Chip origin={c.origin} via={c.via} />
                </span>
                <span className="text-[11px] text-slate-500">{c.status}</span>
                <button onClick={() => setContacts((cur) => cur.filter((x) => x.id !== c.id))} className="text-slate-400 hover:text-rose-500 cursor-pointer" aria-label={`Quitar a ${c.name || c.phone}`}>
                  <i className="ri-close-line"></i>
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-between text-[11px] text-slate-500">
            <span>
              {contacts.filter((c) => c.status === "enviado").length} de {contacts.length} enviados
            </span>
            <button onClick={() => setContacts([])} className="font-semibold text-slate-500 hover:text-rose-500 cursor-pointer">
              Vaciar lista
            </button>
          </div>
        </>
      ) : null}

      <p className="text-[11px] text-slate-400 mt-4">Escribe solo a personas que te conocen. Si alguien te pide no recibir más mensajes, respétalo y no insistas.</p>

      {importing ? (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" aria-label="Importar contactos">
          <div className="fixed inset-0 bg-[#0B1F3A]/70 backdrop-blur-sm" onClick={() => setImporting(false)}></div>
          <div className="relative w-full sm:max-w-xl max-h-[92dvh] flex flex-col bg-white rounded-t-2xl sm:rounded-2xl">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h4 className="font-heading font-bold text-slate-900">Importar contactos</h4>
              <button onClick={() => setImporting(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 cursor-pointer" aria-label="Cerrar">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="px-4 pt-3 flex gap-1.5" role="tablist">
              {([
                ["agenda", "Agenda / archivo"],
                ["pegar", "Pegar lista"],
                ["prospectos", "Mis prospectos"],
              ] as const).map(([k, l]) => (
                <button key={k} role="tab" aria-selected={tab === k} onClick={() => switchTab(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap ${tab === k ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {l}
                </button>
              ))}
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {tab === "agenda" ? (
                <div className="flex flex-wrap gap-2">
                  {canPick ? (
                    <button onClick={pickFromPhone} className="px-3.5 py-2 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap" style={{ background: PURPLE }}>
                      <i className="ri-contacts-book-line mr-1.5"></i>Abrir mi agenda
                    </button>
                  ) : null}
                  <button onClick={() => fileRef.current?.click()} className="px-3.5 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold cursor-pointer whitespace-nowrap">
                    <i className="ri-file-upload-line mr-1.5"></i>Subir archivo .vcf / .csv
                  </button>
                  <input ref={fileRef} type="file" accept=".vcf,.csv,.txt,text/vcard,text/csv,text/plain" className="hidden" onChange={(e) => void onFile(e.target.files?.[0])} />
                  <p className="w-full text-[11px] text-slate-500">
                    {canPick ? "En Android puedes abrir tu agenda directamente. " : "Tu navegador no abre la agenda directamente: exporta tus contactos (vCard o CSV desde Contactos / Google Contactos) y súbelos aquí. "}
                    Después marcas cuáles quieres importar.
                  </p>
                </div>
              ) : null}

              {tab === "pegar" ? (
                <div>
                  <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={5} placeholder={"María Pérez, 300 123 4567\nJuan, +52 55 1234 5678"} className={`${fieldCls} resize-y`} aria-label="Lista de contactos" />
                  <button onClick={readPaste} disabled={!paste.trim()} className="mt-2 px-3.5 py-2 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50" style={{ background: PURPLE }}>
                    Leer lista
                  </button>
                </div>
              ) : null}

              {tab !== "prospectos" && cands.length > 0 ? (
                <div className="mt-4 grid gap-2 sm:grid-cols-2 rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <label className="text-[11px] font-semibold text-slate-600">
                    Etiqueta · ¿de dónde vienen?
                    <select value={batchOrigin} onChange={(e) => applyBatch(e.target.value, batchVia)} className={`${fieldCls} mt-1`}>
                      {Object.entries(ORIGINS).map(([k, o]) => (
                        <option key={k} value={k}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-[11px] font-semibold text-slate-600">
                    Etiqueta · vía de prospección
                    <select value={batchVia} onChange={(e) => applyBatch(batchOrigin, e.target.value)} className={`${fieldCls} mt-1`}>
                      {Object.entries(VIAS).map(([k, l]) => (
                        <option key={k} value={k}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}

              {tab === "prospectos" ? (
                <p className="text-[11px] text-slate-500 mb-2">Tus prospectos (p. ej. captados con el scraper) no traen teléfono: escribe el número de los que quieras contactar por WhatsApp y márcalos.</p>
              ) : null}
              {loadingP ? <p className="text-sm text-slate-500 py-6 text-center">Cargando…</p> : null}
              {impErr ? <p className="text-xs text-rose-600 mt-2">{impErr}</p> : null}

              {cands.length > 0 ? (
                <div className="mt-3">
                  <div className="flex items-center gap-2">
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…" className={fieldCls} aria-label="Buscar" />
                    <button onClick={() => toggleAll(true)} className="text-xs font-semibold text-violet-700 whitespace-nowrap cursor-pointer">
                      Todos
                    </button>
                    <button onClick={() => toggleAll(false)} className="text-xs font-semibold text-slate-500 whitespace-nowrap cursor-pointer">
                      Ninguno
                    </button>
                  </div>
                  <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-100 max-h-72 overflow-y-auto">
                    {visible.map((c) => {
                      const ok = valid(c);
                      return (
                        <li key={c.key} className="px-3 py-2 flex items-start gap-2.5">
                          <input type="checkbox" checked={c.selected} disabled={!ok} onChange={(e) => setCands((cur) => cur.map((x) => (x.key === c.key ? { ...x, selected: e.target.checked } : x)))} className="mt-1 accent-violet-600" aria-label={`Importar a ${c.name || c.phone}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-800 truncate">{c.name || "Sin nombre"}</div>
                            {c.fixed ? (
                              <input
                                value={c.phone}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setCands((cur) => cur.map((x) => (x.key === c.key ? { ...x, phone: v, selected: x.selected && !!normalizePhone(v, dial) } : x)));
                                }}
                                placeholder="WhatsApp (con o sin código de país)"
                                inputMode="tel"
                                className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-xs outline-none focus:border-violet-400"
                                aria-label={`Número de ${c.name}`}
                              />
                            ) : (
                              <div className={`text-[11px] ${ok ? "text-slate-400" : "text-rose-500"}`}>{ok ? `+${normalizePhone(c.phone, dial)}` : "Número no válido"}</div>
                            )}
                            <div className="mt-1">
                              <Chip origin={c.origin} via={c.via} />
                              {c.note ? <span className="ml-1 text-[10px] text-slate-400">{c.note}</span> : null}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-3">
              <span className="text-xs text-slate-500">{selected.length} seleccionado(s)</span>
              <button onClick={doImport} disabled={selected.length === 0} className="px-4 py-2.5 rounded-lg text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50" style={{ background: PURPLE }}>
                Importar seleccionados
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div role="status" className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-[60] px-4 py-2 rounded-full bg-[#0B1F3A] text-white text-sm shadow-lg">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
